import type { components } from "../../api/schema";

type VisualAssets = components["schemas"]["VisualAssets"];
type ReaderAsset = components["schemas"]["ReaderAsset"];

// 公開mapの明示キーだけを参照し、prototypeやpath推論で承認範囲を広げない。
function ownValue<T>(map: Record<string, T>, key: string | undefined): T | undefined {
  return key !== undefined && Object.prototype.hasOwnProperty.call(map, key)
    ? map[key]
    : undefined;
}

function resolveVersionedVariant(
  map: Record<string, Record<string, ReaderAsset>>,
  speakerId: string | undefined,
  actingVariantId: string | undefined,
): string | null {
  const variants = ownValue(map, speakerId);
  return variants === undefined ? null : ownValue(variants, actingVariantId)?.url ?? null;
}

export type MarionStillPath = {
  scenarioKey: string;
  characterKey: string;
  variantKey?: string | undefined;
};

export function normalizeActingVariantId(value: string | undefined): string | null {
  const normalized = value?.trim();
  return normalized === undefined || normalized.length === 0 ? null : normalized;
}

export function parseMarionStillPath(
  portraitUrl: string | null | undefined,
): MarionStillPath | null {
  if (portraitUrl == null) return null;
  const trimmed = portraitUrl.trim();
  if (trimmed.length === 0) return null;
  let pathname = trimmed;
  try {
    pathname = new URL(trimmed, "http://palimia.local").pathname;
  } catch {
    pathname = trimmed;
  }
  const match = /^\/marion-stills\/([^/]+)\/([^/]+)(?:\/([^/]+))?\.png$/.exec(pathname);
  if (match === null) return null;
  const [, scenarioKey, characterKey, variantKey] = match;
  if (scenarioKey === undefined || characterKey === undefined) return null;
  return {
    scenarioKey,
    characterKey,
    ...(variantKey !== undefined ? { variantKey } : {}),
  };
}

export function inferMarionManifestUrl(
  portraitUrl: string | null | undefined,
): string | null {
  const parsed = parseMarionStillPath(portraitUrl);
  if (parsed === null) return null;
  const basePath = `/marion-packs/${parsed.scenarioKey}/${parsed.characterKey}`;
  if (parsed.variantKey !== undefined) {
    return `${basePath}/${parsed.variantKey}/manifest.json`;
  }
  return `${basePath}/manifest.json`;
}

export function resolveMarionPartsManifestUrl(
  portraitUrl: string | null | undefined,
): string | null {
  const parsed = parseMarionStillPath(portraitUrl);
  if (parsed === null) return null;
  return `/marion-packs/${parsed.scenarioKey}/${parsed.characterKey}-parts/manifest.json`;
}

export type MarionRenderMode = "auto" | "parts" | "variants";

export function resolveMarionStandingRenderConfig(
  portraitUrl: string | null | undefined,
  partsModeEnabled: boolean,
  visualAssets?: VisualAssets | undefined,
): { manifestUrl: string | null; renderMode: MarionRenderMode } {
  if (visualAssets?.mode === "versioned") {
    return { manifestUrl: null, renderMode: "auto" };
  }
  if (partsModeEnabled) {
    return {
      manifestUrl: resolveMarionPartsManifestUrl(portraitUrl),
      renderMode: "parts",
    };
  }
  return {
    manifestUrl: inferMarionManifestUrl(portraitUrl),
    renderMode: "auto",
  };
}

export function resolveVariantStandingImageUrl(
  basePortraitUrl: string | null | undefined,
  actingVariantId: string | undefined,
  visualAssets?: VisualAssets | undefined,
  speakerId?: string | undefined,
): string | null {
  if (visualAssets?.mode === "versioned") {
    return actingVariantId === undefined
      ? ownValue(visualAssets.defaultStandingBySpeakerId, speakerId)?.url ?? null
      : resolveVersionedVariant(visualAssets.standingBySpeakerId, speakerId, actingVariantId);
  }
  const variantId = normalizeActingVariantId(actingVariantId);
  if (variantId === null) return null;
  const parsed = parseMarionStillPath(basePortraitUrl);
  if (parsed === null) return null;
  return `/marion-stills/${parsed.scenarioKey}/${parsed.characterKey}/${encodeURIComponent(variantId)}.png`;
}

export function resolveVariantThumbnailUrl(
  basePortraitUrl: string | null | undefined,
  actingVariantId: string | undefined,
  visualAssets?: VisualAssets | undefined,
  speakerId?: string | undefined,
): string | null {
  if (visualAssets?.mode === "versioned") {
    return resolveVariantStandingImageUrl(basePortraitUrl, actingVariantId, visualAssets, speakerId);
  }
  const variantId = normalizeActingVariantId(actingVariantId);
  if (variantId === null) return null;
  const parsed = parseMarionStillPath(basePortraitUrl);
  if (parsed === null) return null;
  return `/portraits/${parsed.scenarioKey}/${parsed.characterKey}/${encodeURIComponent(variantId)}.png`;
}

export function resolveCutinImageUrl(
  basePortraitUrl: string | null | undefined,
  actingVariantId: string | undefined,
  visualAssets?: VisualAssets | undefined,
  speakerId?: string | undefined,
): string | null {
  if (visualAssets?.mode === "versioned") {
    return resolveVersionedVariant(visualAssets.cutinsBySpeakerId, speakerId, actingVariantId);
  }
  const variantId = normalizeActingVariantId(actingVariantId);
  if (variantId === null) return null;
  const parsed = parseMarionStillPath(basePortraitUrl);
  if (parsed === null) return null;
  return `/cutins/${parsed.scenarioKey}/${parsed.characterKey}/${encodeURIComponent(variantId)}.png`;
}
